import { useEffect, useState } from "react";
import { Header, CardsProps, TextContent, Badge, SpaceBetween, ExpandableSection } from "@amzn/awsui-components-react";
import Button from "@amzn/uxm-aws-ui-components-react/lib/src/polaris/button";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { CatalogItem, ErrorProps } from "interfaces";
import VirtualizedCardTable from "../VirtualizedCardTable/VirtualizedCardTable";
import { useCardTable, CardHeaderWithQuantityInput, useCardTableOutput } from "hooks/useCardTable";
import { useNewOrderNavigate } from "hooks/useNewOrder";
import {
    CARD_LOCATION_TEST_ID,
    CARD_SKU_TEST_ID,
    CARD_TAXONOMY_TEST_ID,
    HARDWARE_RESTOCK_CARDS_HEADER_TEST_ID,
    NON_STANDARD_ORDER_BUTTON_TEST_ID,
    STANDARD_ORDER_BUTTON_TEST_ID,
} from "helpers";
import { useLocation, useNavigate } from "react-router-dom";
import { UserPermissions } from "services/user-permissions";
import { PERMISSIONS } from "configuration/config";

const getStringPath: string = "common.component.LocalITHardwareRestockContent";
const getStringPathCard: string = `${getStringPath}.card`;
const getStringPathHeader: string = `${getStringPath}.header`;

/**
 * Represents the different sections that can appear in a card component
 * @enum {string}
 * @readonly
 * @property {string} SKU - The Stock Keeping Unit section of the card
 * @property {string} TAXONOMY - The taxonomy/category section of the card
 */
export enum CardSectionType {
    SKU = "SKU",
    TAXONOMY = "taxonomy",
    LOCATIONS = "locations",
}

/**
 * Returns which sections should be visible in the card based on user permissions
 *
 * @returns {CardSectionType[]} Array of visible card sections
 */
export const getCardSectionsVisible = (): CardSectionType[] => {
    const sections = [CardSectionType.SKU, CardSectionType.TAXONOMY];

    if (UserPermissions.instance.hasPermission(PERMISSIONS.ITSE_FULL_CATALOG_ACCESS)) {
        sections.push(CardSectionType.LOCATIONS);
    }

    return sections;
};

/**
 * Creates a card definition object for rendering hardware item cards
 *
 * @param {function} getQuantity - Function that takes an item ID and returns its current quantity
 * @param {function} updateQuantity - Function that takes an item ID and new value to update the quantity
 * @returns {object} Card definition object containing header and sections configuration
 */
export const getCardDefinition = (
    getQuantity: (item: CatalogItem) => number,
    updateQuantity: (item: CatalogItem, value: number) => void,
): CardsProps.CardDefinition<CatalogItem> => ({
    header: (item: CatalogItem) => (
        <CardHeaderWithQuantityInput
            name={item.name}
            description={item.description}
            value={getQuantity(item)}
            setValue={(value: number) => updateQuantity(item, value)}
            touchpoint="intake-hardware-item-quantity"
        />
    ),
    sections: [
        {
            id: CardSectionType.SKU,
            header: getString(`${getStringPathCard}.SKU`),
            content: (item: CatalogItem) => (
                <TextContent data-testid={CARD_SKU_TEST_ID}>
                    {item.sku && item.sku !== "" ? item.sku : getString(`${getStringPathCard}.noSKU`)}
                </TextContent>
            ),
        },
        {
            id: CardSectionType.TAXONOMY,
            header: getString(`${getStringPathCard}.Taxonomy`),
            content: (item: CatalogItem) => (
                <TextContent data-testid={CARD_TAXONOMY_TEST_ID}>
                    {item.taxonomyId && item.taxonomyId !== ""
                        ? item.taxonomyId
                        : getString(`${getStringPathCard}.noTaxonomy`)}
                </TextContent>
            ),
        },
        {
            id: CardSectionType.LOCATIONS,
            content: (item: CatalogItem) => {
                const rules = item.rules as string[];

                if (!rules || rules.length === 0) {
                    return (
                        <ExpandableSection
                            headerText={getString(`${getStringPathCard}.availableIn`)}
                            data-testid={CARD_LOCATION_TEST_ID}
                        >
                            <Badge color="blue">{getString(`${getStringPathCard}.allCountries`)}</Badge>
                        </ExpandableSection>
                    );
                }

                return (
                    <ExpandableSection
                        headerText={getString(`${getStringPathCard}.availableIn`)}
                        data-testid={CARD_LOCATION_TEST_ID}
                    >
                        <SpaceBetween direction="horizontal" size="xs">
                            {rules.map((rule) => {
                                const parsedRule = JSON.parse(rule);
                                const country = parsedRule.event?.params?.country;
                                return country ? <Badge key={rule}>{country}</Badge> : null;
                            })}
                        </SpaceBetween>
                    </ExpandableSection>
                );
            },
        },
    ],
});

/**
 * Renders a link component for placing non-standard hardware orders
 *
 * @returns {JSX.Element} A Link component with the non-standard order text and URL
 */
export const PlaceNonStandardOrderLink = () => {
    const { navigateToNewOrder } = useNewOrderNavigate({ categories: [] });

    const onSubmitNonStandardOrder = () => {
        const isOrderStandard: boolean = false;
        navigateToNewOrder({ itemsSelected: null, isOrderStandard });
    };

    return (
        <Button
            data-testid={NON_STANDARD_ORDER_BUTTON_TEST_ID}
            onClick={onSubmitNonStandardOrder}
            variant="inline-link"
            touchpoint="intake-place-non-standard-order"
        >
            {getString(`${getStringPathHeader}.nonStandardRequest`)}
        </Button>
    );
};

/**
 * Represent the props for the LocalITHardwareRestockContentHeader component
 * @interface
 * @property {() => void} onSubmitNewOrder - A callback function that is called when a new order is submitted, passing an object with a Map of item IDs and their corresponding quantities
 * @property {Map<number, number>} values - A Map of item IDs and their corresponding quantities
 */
export interface LocalITHardwareRestockContentHeaderProps {
    onSubmitNewOrder: () => void;
    values: Map<CatalogItem, number>;
}

/**
 * Renders a header component for the hardware restock content
 *
 * @param {function} onSubmitNewOrder - Callback function when submit button is clicked
 * @param {Map<number,number>} values - Map of item IDs to their selected quantities
 * @returns {JSX.Element} Header component with counter, submit button and description
 */
export const LocalITHardwareRestockContentHeader = ({
    onSubmitNewOrder,
    values,
}: LocalITHardwareRestockContentHeaderProps) => {
    const [productsSelected, setProductsSelected] = useState(0);
    const [itemsSelected, setItemsSelected] = useState(0);

    useEffect(() => {
        let products = 0;
        let items = 0;
        values.forEach((value) => {
            if (value > 0) {
                products++;
                items += value;
            }
        });
        setProductsSelected(products);
        setItemsSelected(items);
    }, [values]);

    function isButtonAvailable() {
        return !(productsSelected > 0);
    }
    return (
        <Header
            data-testid={HARDWARE_RESTOCK_CARDS_HEADER_TEST_ID}
            counter={getString("counter", getStringPathHeader, {
                productsSelected: productsSelected,
                itemsSelected: itemsSelected,
            })}
            actions={
                <Button
                    data-testid={STANDARD_ORDER_BUTTON_TEST_ID}
                    variant="primary"
                    onClick={onSubmitNewOrder}
                    disabled={isButtonAvailable()}
                    touchpoint="intake-place-standard-order"
                >
                    {getString(`${getStringPathHeader}.orderButton`)}
                </Button>
            }
            description={<PlaceNonStandardOrderLink />}
        >
            {getString(`${getStringPathHeader}.title`)}
        </Header>
    );
};

interface NavigateToHardwareRestock {
    isOrderStandard: boolean;
    standardOrder: Map<CatalogItem, number>;
}

/**
 * Represent the props for the LocalITHardwareRestockContent component
 * @interface
 * @property {CatalogRequestResponse[]} catalog - An array of CatalogRequestResponse objects representing the catalog of items
 * @property {string} trackBy - A string representing the property to track by in the catalog (unique identifier)
 * @property {ErrorProps | undefined} error - An optional Error object representing any error that occurred
 * @property {boolean} isLoading - A boolean indicating whether the component is currently loading data
 * @property {(params: {quantities: Map<CatalogRequestResponse, number>}) => void} onSubmitNewOrder - A callback function that is called when a new order is submitted, passing a Map of CatalogRequestResponse and their corresponding quantities
 * @property {boolean} [hasMore] - A boolean indicating whether there are more items to load
 * @property {() => void} [onLoadMore] - A callback function to load more items
 * @property {boolean} [isLoadingMore] - A boolean indicating whether more items are currently being loaded
 */
export interface LocalITHardwareRestockContentProps {
    catalog: CatalogItem[];
    trackBy: string;
    error?: ErrorProps;
    isLoading: boolean;
    onSubmitNewOrder: (params: { quantities: Map<CatalogItem, number> }) => void;
    hasMore?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
}

/**
 * Renders the hardware restock content component with infinite scrolling support
 *
 * @property {CatalogRequestResponse[]} catalog - An array of CatalogRequestResponse objects representing the catalog of items
 * @property {string} trackBy - A string representing the property to track by in the catalog (unique identifier)
 * @property {Error | undefined} error - An optional Error object representing any error that occurred
 * @property {boolean} isLoading - A boolean indicating whether the component is currently loading data
 * @property {(params: {quantities: Map<number, number>}) => void} onSubmitNewOrder - A callback function that is called when a new order is submitted, passing an object with a Map of item IDs and their corresponding quantities
 * @property {boolean} hasMore - A boolean indicating whether there are more items to load
 * @property {() => void} onLoadMore - A callback function to load more items when scrolling reaches the bottom
 * @property {boolean} isLoadingMore - A boolean indicating whether more items are currently being loaded
 */
const LocalITHardwareRestockContent = ({
    catalog,
    trackBy,
    error,
    isLoading,
    onSubmitNewOrder,
    hasMore,
    onLoadMore,
    isLoadingMore,
}: LocalITHardwareRestockContentProps) => {
    /* Retrieve the items if they went back from the order confirmation page */
    const navigate = useNavigate();
    const location = useLocation();
    const { standardOrder, isOrderStandard }: NavigateToHardwareRestock = location.state || {};

    useEffect(() => {
        navigate(location.pathname + location.search, { replace: true });
    }, [standardOrder, isOrderStandard, navigate, location.pathname, location.search]);

    /* CARDS LAYOUT CONFIGURATION */
    const cardsLayout: CardsProps.CardsLayout[] = [{ cards: 1 }, { minWidth: 800, cards: 2 }];

    const { quantities, getQuantity, updateQuantity, onSubmitNewOrderHandler }: useCardTableOutput = useCardTable({
        onSubmitNewOrder,
        quantities: isOrderStandard ? standardOrder : undefined,
    });

    return (
        <VirtualizedCardTable<CatalogItem>
            cardDefinitions={getCardDefinition(getQuantity, updateQuantity)}
            catalog={catalog}
            cardSectionsVisible={getCardSectionsVisible()}
            loading={isLoading}
            trackBy={trackBy}
            error={error}
            header={
                <LocalITHardwareRestockContentHeader onSubmitNewOrder={onSubmitNewOrderHandler} values={quantities} />
            }
            cardsLayout={cardsLayout}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            isLoadingMore={isLoadingMore}
        />
    );
};

export default LocalITHardwareRestockContent;
